import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Results.css';

const Results = () => {
  const { t } = useTranslation();
  const [animateNumbers, setAnimateNumbers] = useState(false);
  const sectionRef = useRef(null);
  const tagRef = useRef(null);
  const titleRef = useRef(null);
  const statsRef = useRef(null);
  
  // Animation on scroll
  useEffect(() => {
    const section = sectionRef.current;
    const tag = tagRef.current;
    const title = titleRef.current;
    const stats = statsRef.current;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          section.classList.add('visible');
          setTimeout(() => tag.classList.add('visible'), 50);
          setTimeout(() => title.classList.add('visible'), 100);
          setTimeout(() => {
            stats.classList.add('visible');
            setAnimateNumbers(true);
          }, 150);
          
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    
    if (section) {
      observer.observe(section);
    }
    
    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  // Number animation
  const CounterAnimation = ({ value, suffix = '', duration = 800 }) => {
    const [count, setCount] = useState(0);
    const countRef = useRef(null);
    
    useEffect(() => {
      if (!animateNumbers) return;
      
      // Extract just the numeric part for animation
      let startValue = 0;
      let endValue = parseInt(value.replace(/[^0-9]/g, ''));
      
      const stepTime = Math.abs(Math.floor(duration / endValue));
      
      const counter = setInterval(() => {
        startValue += 1;
        setCount(startValue);
        
        if (startValue >= endValue) {
          clearInterval(counter);
        }
      }, stepTime);
      
      return () => clearInterval(counter);
    }, [duration, value]);
    
    return (
      <span ref={countRef}>
        {value.startsWith('+') ? '+' : ''}{count}
      </span>
    );
  };

  return (
    <section className="results" ref={sectionRef}>
      <div className="results-background">
        <div className="results-shape results-shape-1"></div>
        <div className="results-shape results-shape-2"></div>
      </div>
      
      <div className="container">
        <div className="results-content">
          <div className="results-tag" ref={tagRef}>{t('resultsTag')}</div>
          
          <h2 className="results-title" ref={titleRef}>
            {t('resultsTitle')}
          </h2>
          
          <div className="results-stats" ref={statsRef}>
            <div className="result-stat">
              <div className="result-value">
                {animateNumbers ? (
                  <>
                    <CounterAnimation value={t('resultsStat1')} />
                    {t('resultsStat1').includes('%') ? '%' : ''}
                  </>
                ) : (
                  t('resultsStat1')
                )}
              </div>
              <div className="result-divider"></div>
              <div className="result-description">{t('resultsStat1Desc')}</div>
            </div>
            
            <div className="result-stat">
              <div className="result-value">
                {animateNumbers ? (
                  <>
                    <CounterAnimation value={t('resultsStat2')} />
                    {t('resultsStat2').includes('x') ? 'x' : ''}
                  </>
                ) : (
                  t('resultsStat2')
                )}
              </div>
              <div className="result-divider"></div>
              <div className="result-description">{t('resultsStat2Desc')}</div>
            </div>
            
            <div className="result-stat">
              <div className="result-value">
                {animateNumbers ? (
                  <>
                    <CounterAnimation value={t('resultsStat3')} duration={1500} />
                    <span className="result-suffix blue-suffix">h</span>
                  </>
                ) : (
                  t('resultsStat3')
                )}
              </div>
              <div className="result-divider"></div>
              <div className="result-description">{t('resultsStat3Desc')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Results;
